import React from 'react';
import { useAtom } from 'jotai';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { currentUserAtom } from '../store';
import DashboardStats from '../components/DashboardStats';
import { Card } from '../components/ui/Card';
import { Bell, Settings } from 'lucide-react';
import { supabase } from '../lib/supabase';

type DashboardStatsData = {
  totalPatients: number;
  totalAppointments: number;
  syncRate: number;
  apiHealth: string;
  patientSync: {
    total: number;
    received: number;
    failed: number;
    synced: number;
  };
  appointmentSync: {
    total: number;
    received: number;
    failed: number;
    synced: number;
  };
};

export default function Dashboard() {
  const [currentUser] = useAtom(currentUserAtom);

  // Stats (counts + sync rate)
  const { data: stats } = useQuery<DashboardStatsData>({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const userId = session.user.id;

      // Fetch all counts in parallel
      const [
        patientsCountRes,
        appointmentsCountRes,
        patientsReceivedRes,
        patientsFailedRes,
        patientsSyncedRes,
        appointmentsReceivedRes,
        appointmentsFailedRes,
        appointmentsSyncedRes,
      ] = await Promise.all([
        // Total counts
        supabase.from('patients').select('id', { count: 'exact', head: true }).eq('user_id', userId),
        supabase.from('appointments').select('id', { count: 'exact', head: true }).eq('user_id', userId),
        // Patient sync status counts
        supabase.from('patients').select('id', { count: 'exact', head: true }).eq('user_id', userId).eq('zonedm_sync_status', 'received'),
        supabase.from('patients').select('id', { count: 'exact', head: true }).eq('user_id', userId).in('zonedm_sync_status', ['zonedm_error', 'missing_contact']),
        supabase.from('patients').select('id', { count: 'exact', head: true }).eq('user_id', userId).eq('zonedm_sync_status', 'zonedm_synced'),
        // Appointment sync status counts
        supabase.from('appointments').select('id', { count: 'exact', head: true }).eq('user_id', userId).eq('zonedm_sync_status', 'received'),
        supabase.from('appointments').select('id', { count: 'exact', head: true }).eq('user_id', userId).in('zonedm_sync_status', ['zonedm_error', 'no_patient', 'not_mapped', 'waiting_for_patient']),
        supabase.from('appointments').select('id', { count: 'exact', head: true }).eq('user_id', userId).eq('zonedm_sync_status', 'zonedm_synced'),
      ]);

      const totalPatients = patientsCountRes.count || 0;
      const totalAppointments = appointmentsCountRes.count || 0;
      const patientsReceived = patientsReceivedRes.count || 0;
      const patientsFailed = patientsFailedRes.count || 0;
      const patientsSynced = patientsSyncedRes.count || 0;
      const appointmentsReceived = appointmentsReceivedRes.count || 0;
      const appointmentsFailed = appointmentsFailedRes.count || 0;
      const appointmentsSynced = appointmentsSyncedRes.count || 0;

      // Calculate overall sync rate: (total - received) / total * 100
      // This gives us the % that have been processed (not still waiting in "received" state)
      const totalRecords = totalPatients + totalAppointments;
      const totalReceived = patientsReceived + appointmentsReceived;
      const syncRate = totalRecords > 0 ? Math.round(((totalRecords - totalReceived) / totalRecords) * 100) : 100;

      return {
        totalPatients,
        totalAppointments,
        syncRate,
        apiHealth: 'Healthy',
        patientSync: {
          total: totalPatients,
          received: patientsReceived,
          failed: patientsFailed,
          synced: patientsSynced,
        },
        appointmentSync: {
          total: totalAppointments,
          received: appointmentsReceived,
          failed: appointmentsFailed,
          synced: appointmentsSynced,
        },
      };
    },
  });

  // Recent patients
  const { data: recentPatients = [] } = useQuery<any[]>({
    queryKey: ['dashboard-recent-patients'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('patients')
        .select('id, first_name, last_name, email, status, last_sync, updated_at, zonedm_sync_status')
        .eq('user_id', session.user.id)
        .order('updated_at', { ascending: false })
        .limit(5);
      if (error) throw error;
      return data || [];
    },
  });

  // Recent appointments
  const { data: recentAppointments = [] } = useQuery<any[]>({
    queryKey: ['dashboard-recent-appointments'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('appointments')
        .select('id, patient_id, start_time, status, appointment_types(name), zonedm_sync_status, last_sync, patients(first_name, last_name)')
        .eq('user_id', session.user.id)
        .order('start_time', { ascending: false })
        .limit(5);
      if (error) throw error;
      return data || [];
    },
  });

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Welcome back, {currentUser?.name}</h1>
            <p className="mt-1 text-sm text-gray-500">
              Here's your integration status overview.
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <button className="p-2 text-gray-400 hover:text-gray-500 rounded-full hover:bg-gray-100">
              <Bell className="w-6 h-6" />
            </button>
            <button className="p-2 text-gray-400 hover:text-gray-500 rounded-full hover:bg-gray-100">
              <Settings className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Stats */}
        <DashboardStats stats={stats || { 
          totalPatients: 0, 
          totalAppointments: 0, 
          syncRate: 100, 
          apiHealth: 'Healthy',
          patientSync: { total: 0, received: 0, failed: 0, synced: 0 },
          appointmentSync: { total: 0, received: 0, failed: 0, synced: 0 },
        }} />

        {/* Recent Patients */}
        <div className="mt-8">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Recent Patients</h2>
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sync Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sync Date
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recentPatients.map((patient: any) => (
                    <tr key={patient.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {patient.first_name} {patient.last_name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{patient.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          String(patient.status || '').toLowerCase() === 'active'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {String(patient.status || '')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          patient.zonedm_sync_status === 'zonedm_synced'
                            ? 'bg-green-100 text-green-800'
                            : patient.zonedm_sync_status === 'received'
                            ? 'bg-yellow-100 text-yellow-800'
                            : patient.zonedm_sync_status === 'zonedm_error' || patient.zonedm_sync_status === 'missing_contact'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {patient.zonedm_sync_status?.replace('zonedm_', '').replace(/_/g, ' ') || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {patient.last_sync ? format(new Date(patient.last_sync), 'MMM dd, yyyy HH:mm') : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        {/* Recent Appointments */}
        <div className="mt-8">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Recent Appointments</h2>
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Patient
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date & Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sync Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sync Date
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recentAppointments.map((appointment: any) => (
                    <tr key={appointment.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {appointment.patients 
                            ? `${appointment.patients.first_name || ''} ${appointment.patients.last_name || ''}`.trim() || '-'
                            : '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {appointment.start_time ? format(new Date(appointment.start_time), 'MMM dd, yyyy HH:mm') : '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{appointment.appointment_types?.name || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          appointment.status === 'scheduled' 
                            ? 'bg-yellow-100 text-yellow-800'
                            : appointment.status === 'completed'
                            ? 'bg-green-100 text-green-800'
                            : appointment.status === 'cancelled'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {appointment.status?.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          appointment.zonedm_sync_status === 'zonedm_synced'
                            ? 'bg-green-100 text-green-800'
                            : appointment.zonedm_sync_status === 'received'
                            ? 'bg-yellow-100 text-yellow-800'
                            : appointment.zonedm_sync_status === 'zonedm_error' || appointment.zonedm_sync_status === 'no_patient' || appointment.zonedm_sync_status === 'not_mapped'
                            ? 'bg-red-100 text-red-800'
                            : appointment.zonedm_sync_status === 'skipped' || appointment.zonedm_sync_status === 'skipped_archived' || appointment.zonedm_sync_status === 'archived'
                            ? 'bg-gray-100 text-gray-600'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {appointment.zonedm_sync_status?.replace('zonedm_', '').replace(/_/g, ' ') || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {appointment.last_sync ? format(new Date(appointment.last_sync), 'MMM dd, yyyy HH:mm') : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}